
import { useState, useEffect } from 'react';
import { authService } from '../services/authService';

interface FeatureAccess {
  canAccess: boolean;
  isLoading: boolean;
  isDev: boolean;
  showPaywall: () => void;
  remainingUses?: number;
  tier: 'free' | 'premium' | 'elite';
}

export function useFeatureAccess(
  feature: 'exercise' | 'writing_eval' | 'speaking_enhance' | 'mock_test' | 'advanced_module',
  userId: string
): FeatureAccess {
  const [canAccess, setCanAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDev, setIsDev] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [user, setUser] = useState<any>(null);
  
  const checkAccess = async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const userData = await authService.getUserById(userId);
      setUser(userData);

      // --- GLOBAL OVERRIDE: DISABLE PAYWALL ---
      // Until payment gateway is fully detected/integrated, we disable the paywall
      // to allow full access to all users during the preview/beta phase.
      const PAYMENT_GATEWAY_DETECTED = false;
      
      if (!PAYMENT_GATEWAY_DETECTED) {
        setCanAccess(true);
        // Preserve dev status if it exists, otherwise false (though doesn't restrict access)
        setIsDev(userData?.devAccess || false);
        setIsLoading(false);
        return;
      }
      // ----------------------------------------
      
      if (!userData) {
        setCanAccess(false);
        setIsDev(false);
        setIsLoading(false);
        return;
      }

      const devStatus = !!userData.devAccess;
      setIsDev(devStatus);
      
      // If dev, always grant access and bypass limits
      if (devStatus) {
        setCanAccess(true);
        setIsLoading(false);
        return;
      }
      
      // Check feature access based on tier
      const hasAccess = authService.hasTier(userData, 
        feature === 'advanced_module' ? 'premium' : 
        feature === 'writing_eval' && userData.stats.writingEvalsUsed >= 10 ? 'elite' : 
        'free'
      );
      
      // Check daily limits for free users
      if (userData.tier === 'free' && feature === 'exercise') {
        const today = new Date().toISOString().split('T')[0];
        const dailyCount = userData.stats.dailyExerciseCount || 0;
        const lastExerciseDate = userData.stats.lastExerciseDate || '';
        
        if (lastExerciseDate !== today) {
          // Reset count for new day
          await authService.updateUser(userId, {
            'stats.dailyExerciseCount': 0,
            'stats.lastExerciseDate': today
          });
          setCanAccess(true);
        } else {
          setCanAccess(dailyCount < 5);
        }
      } else {
        setCanAccess(hasAccess);
      }
    } catch (error) {
      console.error("Access check failed", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAccess();
  }, [userId, feature]);
  
  return {
    canAccess,
    isLoading,
    isDev,
    showPaywall: () => setShowPaywallModal(true),
    tier: user?.tier || 'free',
    remainingUses: user?.tier === 'free' && feature === 'exercise' 
      ? Math.max(0, 5 - (user?.stats?.dailyExerciseCount || 0))
      : undefined
  };
}
